// src/tabs/logements/ContratTab.jsx — Phase 2.E.9 (ADR-042)
// MVP : génération HTML imprimable du contrat de location + envoi
// par email au voyageur. Pas de signature électronique (YouSign Phase 5).
// Pas de PDF natif (l'utilisateur imprime → Sauver en PDF via navigateur).
//
// DA Groupe 3 / Lot L12 (maquette #5) : reskin VISUEL pur de l'éditeur admin
// (tokens DS, typo Fraunces/Libre Franklin, icônes lucide). Aucun handler,
// réseau, valeur de clause ni data-testid touché — voir le GATE du chantier.

import { useEffect, useState } from "react"
import {
  Info, Target, ScrollText, Languages, Eye, Send, RotateCcw,
  AlertTriangle, AlertCircle, User, Settings, FlaskConical,
  CheckCircle2, Loader2, Lightbulb,
} from "lucide-react"
import { FieldFull, Textarea } from "./atoms"
import { DS } from "../../lib/ui"
import { genContratHTML, DEFAULT_CLAUSES } from "../../lib/contrat-html"
import { nomLogementFacture, logPourFacture, RAISON_CONTRAT_SANS_NOM } from "../../lib/nom-logement-facture"
// LOT A (moteur contrat templatable) — validation live (tokens + structure {#if}) + catalogue.
import { unknownContratTokens, malformedContratConditions, CONTRAT_TOKEN_NAMES, CONTRAT_FLAG_NAMES } from "../../lib/contrat-vars"
import { resolveEmitter } from "../../lib/emitter-resolver"
// CONTRAT V2 (revue M1) — verdict caution de l'aperçu admin câblé sur la
// dérivation unique (miroir client, plateforme 'direct') : montant ∧ matrice.
// Résolution impossible → undefined → rendu historique (mention conservée).
import { resolveContratCautionDueClient } from "../../lib/contrat-caution-apercu"
import { supabase } from "../../supabase"

// ── Atomes d'affichage locaux (UI pure, propres à cet onglet) ───────────────
function IconPill({ icon: Icon, bg = DS.cielSoft, color = DS.marine }) {
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", justifyContent: "center",
      width: 34, height: 34, borderRadius: 9, background: bg, color, flexShrink: 0,
    }}>
      <Icon size={18} strokeWidth={2} />
    </span>
  )
}

function Card({ icon, iconBg, iconColor, title, intro, children }) {
  return (
    <section style={{
      background: DS.card, borderRadius: DS.radiusCard, padding: 18, marginBottom: 14,
      border: `1px solid ${DS.border}`, boxShadow: "0 2px 8px rgba(0,0,0,.05)",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 11, marginBottom: intro ? 8 : 14 }}>
        <IconPill icon={icon} bg={iconBg} color={iconColor} />
        <div style={{ fontFamily: DS.fontDisplay, fontSize: 15, fontWeight: 700, color: DS.marine, letterSpacing: .2 }}>
          {title}
        </div>
      </div>
      {intro && <div style={{ fontSize: 11.5, color: DS.textMuted, marginBottom: 14, lineHeight: 1.55 }}>{intro}</div>}
      {children}
    </section>
  )
}

function LangBadge({ children, bg = DS.cielSoft, color = DS.marine }) {
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 11px",
      borderRadius: 999, background: bg, color, fontSize: 11, fontWeight: 700,
      fontFamily: DS.fontBody, letterSpacing: .3, marginBottom: 10,
    }}>
      <Languages size={13} /> {children}
    </span>
  )
}

const btnBase = {
  display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8,
  borderRadius: DS.radiusControl, padding: "11px 20px", fontSize: 13, fontWeight: 700,
  fontFamily: DS.fontBody, flex: "1 1 160px",
}

// Logement donné au contrat (aperçu + courriel admin) : fusion maison ← chambre
// (adresse, codes… de la maison ; nom de la chambre seule) EN GARDANT le lien
// vers la maison, comme le log aplati du contrat serveur. Exporté pour test.
export function contratLogEffectif(log, parentLog = null) {
  const fusion = logPourFacture(log, parentLog)
  return parentLog && fusion ? { ...fusion, parentLog } : fusion
}

export default function ContratTab({ log, parentLog = null, prop, propNom, cfg, onUpdate }) {
  // Si chambre sous-loc : merge superficiel parentLog ← log (adresse, codes,
  // wifi, contact viennent du parent). Cf. fix C-3. Le NOM et le NOM COMPLET,
  // eux, sont ceux de la chambre SEULE (30/09, même règle que les factures) :
  // une maison avec un nom complet ne le prête plus à sa chambre.
  //
  // Le lien vers la maison (`parentLog`) est GARDÉ (07/10, relecture argent) :
  // la fusion seule écrasait la caution de la maison par un champ vidé de la
  // chambre (null) — l'aperçu et le courriel affichaient « ________ € » et
  // « aucun dépôt demandé » alors que Stripe préautorise la caution de la
  // maison. Avec parentLog, le contrat lit la même règle que Stripe (champ
  // vide → maison) et que le contrat serveur (log aplati, parentLog compris).
  const effectiveLog = contratLogEffectif(log, parentLog)
  const nomContrat = nomLogementFacture(effectiveLog)
  // Architecture multi-émetteur (06/05/2026, Bug #1) : sous-loc = FBZ SAS,
  // sinon = propriétaire. Le bandeau warning UX alerte si adresse vide.
  const emitter = resolveEmitter({ cfg, prop })
  const [recentResas, setRecentResas] = useState([])
  const [loading, setLoading] = useState(false)
  const [selectedResaId, setSelectedResaId] = useState("")
  const [sending, setSending] = useState(false)
  const [info, setInfo] = useState(null)
  const [error, setError] = useState(null)

  // Charge les 20 dernières résas du logement (V2 reservations)
  useEffect(() => {
    let mounted = true
    setLoading(true)
    supabase.from("reservations")
      .select("id, guest_first_name, guest_last_name, guest_email, guest_phone, guest_count, start_date, end_date, total_amount, source, status")
      .eq("logement_id", log.id)
      .order("start_date", { ascending: false })
      .limit(20)
      .then(({ data, error: e }) => {
        if (!mounted) return
        if (e) setError(`Chargement résas : ${e.message}`)
        else setRecentResas(data || [])
        setLoading(false)
      })
    return () => { mounted = false }
  }, [log.id])

  const selectedResa = recentResas.find(r => r.id === selectedResaId) || null

  async function handlePreview() {
    setError(null); setInfo(null)
    // Sans nom propre, pas de contrat : jamais le nom de la maison, jamais « lc2 ».
    if (!nomContrat) { setError(RAISON_CONTRAT_SANS_NOM); return }
    const cautionDue = await resolveContratCautionDueClient(effectiveLog)
    const { html } = genContratHTML({
      log: effectiveLog,
      reservation: selectedResa,
      cfg,
      prop,
      mode: "preview",
      cautionDue,
    })
    const win = window.open("", "_blank")
    if (!win) { setError("Le popup a été bloqué — autorise les popups pour ce site."); return }
    win.document.write(html); win.document.close()
  }

  async function handleSendEmail() {
    setError(null); setInfo(null)
    if (!selectedResa) { setError("Sélectionner d'abord une résa pour envoyer le contrat."); return }
    if (!nomContrat) { setError(RAISON_CONTRAT_SANS_NOM); return }
    const dest = selectedResa.guest_email
    if (!dest) { setError("Cette résa n'a pas d'email voyageur. Ajoute-le manuellement ou choisis une autre résa."); return }
    setSending(true)
    try {
      const cautionDue = await resolveContratCautionDueClient(effectiveLog)
      const { html } = genContratHTML({
        log: effectiveLog,
        reservation: selectedResa,
        cfg,
        prop,
        mode: "email",
        cautionDue,
      })
      const subject = `Votre contrat de location — ${nomContrat}`

      const { data: { session } } = await supabase.auth.getSession()
      const token = session?.access_token
      if (!token) throw new Error("Pas de session admin active")

      const r = await fetch("/api/send-email", {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
        body: JSON.stringify({ type: "contrat_send", data: { to: dest, subject, html, logementId: log.id, reservationId: selectedResa.id } }),
      })
      const j = await r.json().catch(() => ({}))
      if (!r.ok) {
        throw new Error(j.error || `HTTP ${r.status}`)
      }
      // BUG 1 — toast honnête : si le serveur a simulé (MESSAGES_DRY_RUN), on
      // le dit clairement au lieu d'afficher un faux « envoyé ». L'icône/pastille
      // de rendu (🧪 → FlaskConical / ✅ → CheckCircle2) est choisie à l'affichage.
      setInfo(j.dryRun
        ? `Mode test (DRY_RUN) — contrat NON envoyé à ${dest}.`
        : `Contrat envoyé à ${dest}`)
    } catch (e) {
      setError(e.message || String(e))
    } finally { setSending(false) }
  }

  const sendDisabled = sending || !selectedResa
  const infoIsTest = info ? info.includes("Mode test") : false
  const InfoIcon = infoIsTest ? FlaskConical : CheckCircle2

  // LOT A — tokens inconnus + blocs {#if} malformés des clauses saisies
  // (validation LIVE, miroir de la validation BLOQUANTE serveur /api/cfg : le
  // save sera refusé tant qu'il en reste — on prévient l'admin avant qu'il ne
  // clique Enregistrer). M1 : un {#if} non fermé / une expression invalide est
  // aussi grave qu'un token inconnu (texte juridique rendu ou masqué à tort).
  const clausesUnknownTokens = unknownContratTokens(log.contrat_clauses || "")
  const clausesMalformed = malformedContratConditions(log.contrat_clauses || "")

  return (
    <div>
      <style>{`@keyframes fbzContratSpin{to{transform:rotate(360deg)}}`}</style>

      {/* Bandeau info ciel */}
      <div style={{
        display: "flex", gap: 11, padding: "13px 15px", background: DS.cielSoft,
        border: `1px solid ${DS.ciel}`, borderRadius: DS.radiusControl, fontSize: 12,
        color: DS.marineDeep, marginBottom: 14, lineHeight: 1.55,
      }}>
        <Info size={17} color={DS.marine} style={{ flexShrink: 0, marginTop: 1 }} />
        <span>Contrat HTML imprimable (Sauver en PDF via le navigateur) + envoi par email au voyageur. Sur le parcours direct, la signature électronique maison est active (lien de signature, PDF horodaté sha256).</span>
      </div>

      {/* Bandeau warning émetteur si adresse vide */}
      {!emitter.adresse && (
        <div style={{
          display: "flex", gap: 11, padding: "13px 15px", background: DS.warningBg,
          border: `1px solid ${DS.warning}`, borderRadius: DS.radiusControl, fontSize: 12,
          color: DS.warningText, marginBottom: 14, lineHeight: 1.55,
        }}>
          <AlertTriangle size={17} color={DS.warning} style={{ flexShrink: 0, marginTop: 1 }} />
          <span>
            <strong>Adresse {emitter.type === 'fbz' ? 'FBZ SAS' : 'propriétaire'} vide.</strong>{' '}
            {emitter.type === 'proprio' ? (
              <>Renseigne-la dans <strong>Info FBZ → <User size={13} style={{ verticalAlign: "-2px" }} /> Propriétaire</strong> pour la conformité légale du document.</>
            ) : (
              <>Renseigne-la dans <strong>Paramètres → <Settings size={13} style={{ verticalAlign: "-2px" }} /> Général → Informations société</strong> pour la conformité légale.</>
            )}
          </span>
        </div>
      )}

      {/* Sélection résa — présentée en carte */}
      <Card
        icon={Target}
        title="Choisir une résa (optionnel)"
        intro="Le contrat se pré-remplit avec les données de la résa sélectionnée. Sans résa : contrat type avec champs vides à remplir manuellement."
      >
        {loading ? (
          <div style={{ fontSize: 12, color: DS.textMuted, padding: "8px 0" }}>Chargement des dernières résas…</div>
        ) : recentResas.length === 0 ? (
          <div style={{ fontSize: 12, color: DS.textMuted, padding: "8px 0", fontStyle: "italic" }}>Aucune résa V2 (Beds24) pour ce logement.</div>
        ) : (
          <select value={selectedResaId} onChange={e => setSelectedResaId(e.target.value)}
            style={{ width: "100%", padding: "10px 13px", border: `1px solid ${DS.borderHover}`, borderRadius: DS.radiusControl, fontSize: 13, background: DS.card, fontFamily: DS.fontBody, cursor: "pointer", color: DS.textPrimary }}>
            <option value="">— Contrat type (sans résa) —</option>
            {recentResas.map(r => {
              const nom = [r.guest_first_name, r.guest_last_name].filter(Boolean).join(" ") || "(sans nom)"
              const dates = `${r.start_date} → ${r.end_date}`
              const meta = `${r.source || "?"} · ${r.status || "?"}`
              return <option key={r.id} value={r.id}>{nom} · {dates} · {meta}</option>
            })}
          </select>
        )}
        {selectedResa && (
          <div style={{ marginTop: 10, padding: "10px 13px", background: DS.cielSoft, borderRadius: DS.radiusControl, fontSize: 11.5, color: DS.marineDeep, lineHeight: 1.6, border: `1px solid ${DS.ciel}` }}>
            <strong>{[selectedResa.guest_first_name, selectedResa.guest_last_name].filter(Boolean).join(" ")}</strong> ·
            {selectedResa.guest_email ? ` ${selectedResa.guest_email}` : ' (sans email)'} ·
            {selectedResa.start_date} → {selectedResa.end_date} ·
            {selectedResa.guest_count || 1} pers. ·
            {selectedResa.total_amount != null ? ` ${Number(selectedResa.total_amount).toFixed(2)} €` : ' (sans montant)'}
          </div>
        )}
      </Card>

      {/* Clauses éditables (FR) */}
      <Card
        icon={ScrollText}
        title="Clauses du contrat (édition)"
        intro="Texte affiché dans la section 'Conditions générales' du contrat. Sauvegardé par logement (cfg.props[].logs[].contrat_clauses). Vide = contrat v2 par défaut (17 articles). Variables {nom_de_variable} et blocs conditionnels {#if animaux_non}…{/if} supportés — une variable inconnue bloque l'enregistrement. Mise en forme : « Article N — Titre » en tête de paragraphe = titre, lignes « | Libellé | Valeur » = tableau, lignes « - … » = liste à puces."
      >
        <div><LangBadge>FR · Français</LangBadge></div>
        <FieldFull label="Clauses (texte libre, sauts de ligne préservés)">
          <Textarea value={log.contrat_clauses || ""} onChange={v => onUpdate("contrat_clauses", v)}
            placeholder="Vide = clauses par défaut FBZ" rows={14} />
        </FieldFull>
        {(clausesUnknownTokens.length > 0 || clausesMalformed.length > 0) && (
          <div style={{ display: "flex", gap: 9, alignItems: "flex-start", marginTop: 8, padding: "11px 14px", background: DS.dangerBg, color: DS.dangerText, borderRadius: DS.radiusControl, fontSize: 12, lineHeight: 1.5, border: `1px solid ${DS.danger}` }}>
            <AlertCircle size={16} style={{ flexShrink: 0, marginTop: 1 }} />
            <span>
              {clausesUnknownTokens.length > 0 && (
                <><strong>Variable(s) inconnue(s) :</strong> {clausesUnknownTokens.map(t => `{${t}}`).join(", ")}. </>
              )}
              {clausesMalformed.length > 0 && (
                <><strong>Bloc(s) {"{#if}"} malformé(s) :</strong> {clausesMalformed.join(" ; ")}. </>
              )}
              L'enregistrement sera refusé tant que ces problèmes sont présents (un contrat signé ne doit ni afficher un texte conditionnel à tort, ni en masquer un en silence).
            </span>
          </div>
        )}
        <details style={{ marginTop: 10, fontSize: 11.5, color: DS.textMuted, lineHeight: 1.6 }}>
          <summary style={{ cursor: "pointer", fontWeight: 600 }}>Variables disponibles</summary>
          <div style={{ marginTop: 6 }}>
            {CONTRAT_TOKEN_NAMES.map(n => `{${n}}`).join(" · ")}
            <div style={{ marginTop: 4 }}>
              Blocs conditionnels : {CONTRAT_FLAG_NAMES.map(n => `{#if ${n}}…{/if}`).join(" · ")}
            </div>
          </div>
        </details>
        <button onClick={() => onUpdate("contrat_clauses", DEFAULT_CLAUSES)}
          style={{ display: "inline-flex", alignItems: "center", gap: 7, background: DS.card, color: DS.textSecondary, border: `1px solid ${DS.border}`, borderRadius: DS.radiusControl, padding: "8px 14px", fontSize: 11.5, fontWeight: 600, cursor: "pointer", fontFamily: DS.fontBody, marginTop: 4 }}>
          <RotateCcw size={14} /> Charger les clauses par défaut
        </button>
      </Card>

      {/* LOT 4 (décision Dimitri 2026-08-28) — la carte « Clauses (EN) » est
          SUPPRIMÉE : le champ était édité mais JAMAIS servi (le contrat signé
          est toujours en français, seule version juridiquement engageante).
          La donnée cfg `contrat_clauses_en` existante reste en place, morte. */}

      {/* Actions */}
      <Card icon={Send} iconBg={DS.corailSoft} iconColor={DS.corailText} title="Génération">
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <button onClick={handlePreview}
            style={{ ...btnBase, background: DS.card, color: DS.marine, border: `1px solid ${DS.borderHover}`, cursor: "pointer" }}>
            <Eye size={16} /> Aperçu / Imprimer
          </button>
          <button onClick={handleSendEmail} disabled={sendDisabled}
            title={!selectedResa ? "Sélectionne d'abord une résa" : ""}
            style={{ ...btnBase, background: sendDisabled ? DS.borderHover : DS.corail, color: sendDisabled ? DS.textMuted : "#fff", border: "none", cursor: sendDisabled ? "not-allowed" : "pointer" }}>
            {sending
              ? <><Loader2 size={16} style={{ animation: "fbzContratSpin .8s linear infinite" }} /> Envoi…</>
              : <><Send size={16} /> Envoyer au voyageur</>}
          </button>
        </div>
        {error && (
          <div style={{ display: "flex", gap: 9, alignItems: "flex-start", marginTop: 12, padding: "11px 14px", background: DS.dangerBg, color: DS.dangerText, borderRadius: DS.radiusControl, fontSize: 12, lineHeight: 1.5, border: `1px solid ${DS.danger}` }}>
            <AlertCircle size={16} style={{ flexShrink: 0, marginTop: 1 }} /> <span>{error}</span>
          </div>
        )}
        {info && (
          <div style={{ display: "flex", gap: 9, alignItems: "flex-start", marginTop: 12, padding: "11px 14px", background: infoIsTest ? DS.warningBg : DS.successBg, color: infoIsTest ? DS.warningText : DS.successText, borderRadius: DS.radiusControl, fontSize: 12, lineHeight: 1.5, border: `1px solid ${infoIsTest ? DS.warning : DS.success}` }}>
            <InfoIcon size={16} style={{ flexShrink: 0, marginTop: 1 }} /> <span>{info}</span>
          </div>
        )}
      </Card>

      <div style={{ display: "flex", gap: 10, alignItems: "flex-start", marginTop: 8, fontSize: 11, color: DS.textMuted, lineHeight: 1.55, padding: "11px 13px", background: DS.pageBg, borderRadius: DS.radiusControl, border: `1px solid ${DS.border}` }}>
        <Lightbulb size={15} style={{ flexShrink: 0, marginTop: 1 }} />
        <span>Le contrat utilise les valeurs : nom logement (Info FBZ), caution (Administration), horaires et adresse (Livret › Instructions). Variables manquantes affichées en blancs à compléter manuellement après impression.</span>
      </div>
    </div>
  )
}
